import { Suspense, lazy, type ReactNode } from "react";
import Hero from "@/components/Hero";
import Services from "@/components/Services";
import HeroACSmallimage from "@/components/HeroACSmallimage";
import HeroWFullBG from "@/components/HeroWFullBG";
import PageHero from "@/components/PageHero";
import SectionTitle from "@/components/SectionTitle";
import TechnicalFeature from "@/components/TechnicalFeature";
import RevealOnScroll from "@/components/RevealOnScroll";
import type { HomePageSection } from "@/lib/homepage";

const SectionSolutionsTabs = lazy(() => import("@/components/SolutionsTabs"));
const SectionGrowth = lazy(() => import("@/components/Growth"));
const SectionOnePlatform = lazy(() => import("@/components/OnePlatform"));
const SectionIntegrations = lazy(() => import("@/components/Integrations"));
const SectionProcessFlowTabs = lazy(() => import("@/components/ProcessFlowTabs"));
const SectionVideoOverlayCards = lazy(() => import("@/components/VideoWithOverlayCards"));
const SectionPlatformMapping = lazy(() => import("@/components/PlatformMapping"));
const SectionProgressSteps = lazy(() => import("@/components/ProgressSteps"));
const SectionFeaturedOverlayCards = lazy(() => import("@/components/FeaturedOverlayCards"));
const SectionFeatureCards4Col = lazy(() => import("@/components/FeatureCards4Col"));
const SectionFeatureCards3Col = lazy(() => import("@/components/FeatureCards3Col"));
const SectionTechnicalFeatures2Rows = lazy(() => import("@/components/TechnicalFeatures2rows"));
const SectionTechnicalFeatures3Rows = lazy(() => import("@/components/TechnicalFeatures3rows"));
const SectionBenefitFeatures2Rows = lazy(() => import("@/components/BenefitFeatures2rows"));
const SectionPartnerProgram = lazy(() => import("@/components/PartnerProgram"));
const SectionIconCards4 = lazy(() => import("@/components/Iconcards4"));
const SectionBentoBoxShort = lazy(() => import("@/components/BentoBoxShort"));
const SectionBentoBoxLong = lazy(() => import("@/components/BentoBoxLong"));
const SectionDemoVideoCard = lazy(() => import("@/components/DemoVideoCard"));

function SectionFallback({ type }: { type: HomePageSection["type"] }) {
  return (
    <div
      aria-hidden
      className="min-h-[240px] animate-pulse rounded-3xl border border-white/10 bg-white/5"
    >
      <span className="sr-only">Loading {type} section…</span>
    </div>
  );
}

export function orderSections(sections: HomePageSection[]) {
  if (sections.length <= 1) return sections;
  return [...sections];
}

export function renderSection(section: HomePageSection, index: number) {
  const baseDelay = index === 0 ? 0 : Math.min(index * 0.05, 0.25);
  const key = `${section.type}-${index}`;

  // Hero sections need to break out of main padding to be full-bleed
  const isFullBleed = ["hero", "hero_small_image", "hero_full_bg", "page_hero"].includes(section.type);
  const isFirstSection = index === 0;

  const wrap = (node: ReactNode, delay = baseDelay) => (
    <RevealOnScroll
      key={key}
      as="section"
      delay={delay}
      className={
        isFullBleed
          ? `w-screen -mx-4 sm:-mx-6 md:-mx-8 ${isFirstSection ? "-mt-10" : ""}`
          : "w-full"
      }
    >
      {node}
    </RevealOnScroll>
  );

  switch (section.type) {
    case "hero":
      return wrap(<Hero data={section.data} />, 0);
    case "hero_small_image":
      return wrap(<HeroACSmallimage data={section.data} />);
    case "hero_full_bg":
      return wrap(<HeroWFullBG data={section.data} />);
    case "page_hero":
      return wrap(<PageHero data={section.data} />);
    case "services_grid":
      return wrap(<Services data={section.data} />);
    case "section_title":
      return wrap(<SectionTitle data={section.data} />);
    case "demo_video_card":
      return wrap(
        (
          <Suspense fallback={<SectionFallback type="demo_video_card" />}>
            <SectionDemoVideoCard data={section.data} />
          </Suspense>
        ),
      );
    case "solutions_tabs":
      return wrap(
        (
          <Suspense fallback={<SectionFallback type="solutions_tabs" />}>
            <SectionSolutionsTabs data={section.data} />
          </Suspense>
        ),
      );
    case "growth":
      return wrap(
        (
          <Suspense fallback={<SectionFallback type="growth" />}>
            <SectionGrowth data={section.data} />
          </Suspense>
        ),
      );
    case "one_platform":
      return wrap(
        (
          <Suspense fallback={<SectionFallback type="one_platform" />}>
            <SectionOnePlatform data={section.data} />
          </Suspense>
        ),
      );
    case "integrations":
      return wrap(
        (
          <Suspense fallback={<SectionFallback type="integrations" />}>
            <SectionIntegrations data={section.data} />
          </Suspense>
        ),
      );
    case "process_flow_tabs":
      return wrap(
        (
          <Suspense fallback={<SectionFallback type="process_flow_tabs" />}>
            <SectionProcessFlowTabs data={section.data} />
          </Suspense>
        ),
      );
    case "technical_feature":
      return wrap(<TechnicalFeature data={section.data} />);
    case "video_with_overlay_cards":
      return wrap(
        (
          <Suspense fallback={<SectionFallback type="video_with_overlay_cards" />}>
            <SectionVideoOverlayCards data={section.data} />
          </Suspense>
        ),
      );
    case "platform_mapping":
      return wrap(
        (
          <Suspense fallback={<SectionFallback type="platform_mapping" />}>
            <SectionPlatformMapping data={section.data} />
          </Suspense>
        ),
      );
    case "progress_steps":
      return wrap(
        (
          <Suspense fallback={<SectionFallback type="progress_steps" />}>
            <SectionProgressSteps data={section.data} />
          </Suspense>
        ),
      );
    case "featured_overlay_cards":
      return wrap(
        (
          <Suspense fallback={<SectionFallback type="featured_overlay_cards" />}>
            <SectionFeaturedOverlayCards data={section.data} />
          </Suspense>
        ),
      );
    case "feature_cards_4col":
      return wrap(
        (
          <Suspense fallback={<SectionFallback type="feature_cards_4col" />}>
            <SectionFeatureCards4Col data={section.data} />
          </Suspense>
        ),
      );
    case "feature_cards_3col":
      return wrap(
        (
          <Suspense fallback={<SectionFallback type="feature_cards_3col" />}>
            <SectionFeatureCards3Col data={section.data} />
          </Suspense>
        ),
      );
    case "technical_features_2rows":
      return wrap(
        (
          <Suspense fallback={<SectionFallback type="technical_features_2rows" />}>
            <SectionTechnicalFeatures2Rows data={section.data} />
          </Suspense>
        ),
      );
    case "technical_features_3rows":
      return wrap(
        (
          <Suspense fallback={<SectionFallback type="technical_features_3rows" />}>
            <SectionTechnicalFeatures3Rows data={section.data} />
          </Suspense>
        ),
      );
    case "benefit_features_2rows":
      return wrap(
        (
          <Suspense fallback={<SectionFallback type="benefit_features_2rows" />}>
            <SectionBenefitFeatures2Rows data={section.data} />
          </Suspense>
        ),
      );
    case "partner_program":
      return wrap(
        (
          <Suspense fallback={<SectionFallback type="partner_program" />}>
            <SectionPartnerProgram data={section.data} />
          </Suspense>
        ),
      );
    case "icon_cards_4":
      return wrap(
        (
          <Suspense fallback={<SectionFallback type="icon_cards_4" />}>
            <SectionIconCards4 data={section.data} />
          </Suspense>
        ),
      );
    case "bento_box_short":
      return wrap(
        (
          <Suspense fallback={<SectionFallback type="bento_box_short" />}>
            <SectionBentoBoxShort data={section.data} />
          </Suspense>
        ),
      );
    case "bento_box_long":
      return wrap(
        (
          <Suspense fallback={<SectionFallback type="bento_box_long" />}>
            <SectionBentoBoxLong data={section.data} />
          </Suspense>
        ),
      );
    default:
      return null;
  }
}
